/**
 * Companions Data Hook
 * 
 * Centralized hook for fetching and managing companion data.
 * Handles caching, error states, and data transformations.
 */

import { useState, useEffect, useCallback } from 'react';
import { CompanionAdapter, ApiCompanionListResponse, NormalizedCompanion } from '../adapters/companionAdapter';
import { ChatbotPersonality } from '../types/chatbot';
import { cacheService } from '../services/cacheService';
import { API_BASE_URL } from '../services/api';

interface UseCompanionsOptions {
  useCache?: boolean;
  category?: string;
  autoFetch?: boolean;
}

interface UseCompanionsReturn {
  // Data
  companions: NormalizedCompanion[];
  chatbots: ChatbotPersonality[];
  
  // Loading states
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchCompanions: () => Promise<void>;
  refreshCompanions: () => Promise<void>;
  getCompanionById: (id: string) => NormalizedCompanion | undefined;
  getChatbotById: (id: string) => ChatbotPersonality | undefined;
  
  // Filtering
  filterByCategory: (category: string) => NormalizedCompanion[];
  searchCompanions: (query: string) => NormalizedCompanion[];
}

export const useCompanions = (options: UseCompanionsOptions = {}): UseCompanionsReturn => {
  const { useCache = true, autoFetch = true } = options;
  
  const [companions, setCompanions] = useState<NormalizedCompanion[]>([]);
  const [chatbots, setChatbots] = useState<ChatbotPersonality[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch companions from API
   */
  const fetchCompanions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check cache first
      if (useCache) {
        const cached = cacheService.getCompanions();
        if (cached) {
          console.log('🚀 Using cached companions data');
          const normalizedCompanions = CompanionAdapter.apiResponseToNormalized(cached);
          const chatbotPersonalities = CompanionAdapter.apiResponseToChatbots(cached);
          
          setCompanions(normalizedCompanions);
          setChatbots(chatbotPersonalities);
          setLoading(false);
          return;
        }
      }

      // Fetch from API with fallback endpoints
      const endpoints = [
        '/api/companions',
        '/api/companions/',
        '/api/companions/public',
        '/api/companions/public?include_category=true'
      ];

      let apiResponse: ApiCompanionListResponse | null = null;
      let lastError: Error | null = null;

      for (const endpoint of endpoints) {
        try {
          const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            headers: {
              'Content-Type': 'application/json',
              ...(localStorage.getItem('access_token') && {
                Authorization: `Bearer ${localStorage.getItem('access_token')}`
              })
            }
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          apiResponse = await response.json();
          console.log(`✅ Successfully fetched companions from ${endpoint}`);
          break;
        } catch (err) {
          console.warn(`❌ Failed to fetch from ${endpoint}:`, err);
          lastError = err as Error;
          continue;
        }
      }

      if (!apiResponse) {
        throw lastError || new Error('All companion endpoints failed');
      }

      // Transform data using adapter
      const normalizedCompanions = CompanionAdapter.apiResponseToNormalized(apiResponse);
      const chatbotPersonalities = CompanionAdapter.apiResponseToChatbots(apiResponse);

      // Update state
      setCompanions(normalizedCompanions);
      setChatbots(chatbotPersonalities);

      // Cache the result
      if (useCache) {
        cacheService.setCompanions(apiResponse);
      }

      console.log(`📊 Loaded ${normalizedCompanions.length} companions`);

    } catch (err: any) {
      console.error('Failed to fetch companions:', err);
      
      // Set appropriate error message
      if (err.message?.includes('Network error') || err.message?.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else if (err.message?.includes('401') || err.message?.includes('403')) {
        setError('Authentication required. Please sign in to view companions.');
      } else {
        setError(err.message || 'Failed to load companions. Please try again later.');
      }
      
      // Set empty arrays on error
      setCompanions([]);
      setChatbots([]);
    } finally {
      setLoading(false);
    }
  }, [useCache]);

  /**
   * Refresh companions (bypass cache)
   */
  const refreshCompanions = useCallback(async () => {
    if (useCache) {
      cacheService.clearCompanions();
    }
    await fetchCompanions();
  }, [fetchCompanions, useCache]);

  /**
   * Get companion by ID
   */
  const getCompanionById = useCallback((id: string): NormalizedCompanion | undefined => {
    return companions.find(companion => companion.id === id);
  }, [companions]);

  /**
   * Get chatbot by ID
   */
  const getChatbotById = useCallback((id: string): ChatbotPersonality | undefined => {
    return chatbots.find(chatbot => chatbot.id === id);
  }, [chatbots]);

  /**
   * Filter companions by category
   */
  const filterByCategory = useCallback((category: string): NormalizedCompanion[] => {
    if (!category || typeof category !== 'string') return companions;

    return companions.filter(companion => {
      // Handle 2x2 category system (e.g., "female-realistic")
      if (category.includes('-')) {
        const [gender, style] = category.split('-');
        const hasGender = companion.categories.includes(gender === 'female' ? 'Female' : 'Male');
        const hasStyle = companion.categories.includes(style === 'realistic' ? 'Realistic' : 'Anime');
        return hasGender && hasStyle;
      }

      // Legacy category filtering
      switch (category.toLowerCase()) {
        case 'girls':
        case 'female-realistic':
          return companion.categories.includes('Female') && companion.categories.includes('Realistic');
        case 'anime':
        case 'female-anime':
          return companion.categories.includes('Female') && companion.categories.includes('Anime');
        case 'guys':
        case 'male-realistic':
          return companion.categories.includes('Male') && companion.categories.includes('Realistic');
        case 'male-anime':
          return companion.categories.includes('Male') && companion.categories.includes('Anime');
        default:
          return companion.categories.some(cat => 
            cat.toLowerCase().includes(category.toLowerCase())
          );
      }
    });
  }, [companions]);

  /**
   * Search companions by name or description
   */
  const searchCompanions = useCallback((query: string): NormalizedCompanion[] => {
    if (!query.trim()) return companions;

    const searchTerm = query.toLowerCase().trim();
    return companions.filter(companion => 
      companion.displayName.toLowerCase().includes(searchTerm) ||
      companion.description.toLowerCase().includes(searchTerm) ||
      companion.categories.some(cat => cat.toLowerCase().includes(searchTerm))
    );
  }, [companions]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchCompanions();
    }
  }, [autoFetch, fetchCompanions]);

  return {
    // Data
    companions,
    chatbots,
    
    // Loading states
    loading,
    error,
    
    // Actions
    fetchCompanions,
    refreshCompanions,
    getCompanionById,
    getChatbotById,
    
    // Filtering
    filterByCategory,
    searchCompanions
  };
};

/**
 * Hook for fetching a single companion by ID
 */
export const useCompanion = (companionId: string) => {
  const [companion, setCompanion] = useState<NormalizedCompanion | null>(null);
  const [chatbot, setChatbot] = useState<ChatbotPersonality | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCompanion = useCallback(async () => {
    if (!companionId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/companions/${companionId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('access_token') && {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const apiCompanion = await response.json();
      
      // Transform using adapter
      const normalizedCompanion = CompanionAdapter.apiToNormalized(apiCompanion);
      const chatbotPersonality = CompanionAdapter.normalizedToChatbot(normalizedCompanion);

      setCompanion(normalizedCompanion);
      setChatbot(chatbotPersonality);

    } catch (err: any) {
      console.error(`Failed to fetch companion ${companionId}:`, err);
      setError(err.message || 'Failed to load companion');
      setCompanion(null);
      setChatbot(null);
    } finally {
      setLoading(false);
    }
  }, [companionId]);

  useEffect(() => {
    fetchCompanion();
  }, [fetchCompanion]);

  return {
    companion,
    chatbot,
    loading,
    error,
    refetch: fetchCompanion
  };
};